/**
 * Chart utilities for fetching and transforming chart data
 * for use with lightweight-charts library
 */

import { logger } from './logger';

// Backend data format from the API
export interface ChartDataPoint {
  time: string; // e.g., "2025-10-08 00:00:00.000000000-04:00"
  Open: number;
  High: number;
  Low: number;
  Close: number;
  Volume: number;
  Dividends: number;
  "Stock Splits": number;
}

// API response wrapper
export interface ChartApiResponse {
  id: string;
  data: ChartDataPoint[];
}

// Candlestick data format for lightweight-charts
export interface CandlestickData {
  time: string; // ISO date string like "2025-10-08"
  open: number;
  high: number;
  low: number;
  close: number;
}

// Line chart data format for lightweight-charts
export interface LineData {
  time: string; // ISO date string like "2025-10-08"
  value: number;
}

/**
 * Parses a timestamp string and extracts the ISO date portion
 * Input: "2025-10-08 00:00:00.000000000-04:00"
 * Output: "2025-10-08"
 */
export function parseTimeString(time: string): string {
  // Extract just the date portion (YYYY-MM-DD)
  const dateMatch = time.match(/^\d{4}-\d{2}-\d{2}/);
  if (!dateMatch) {
    logger.warn(`Unable to parse time string: ${time}, using as-is`);
    return time;
  }
  return dateMatch[0];
}

/**
 * Fetches chart data from the backend API via Next.js API proxy
 * @param dataId - UUID for the chart data
 * @param sessionId - Optional session ID for the request
 * @param userId - Optional user ID for the request
 * @returns Raw chart data from the backend
 * @throws Error if fetch fails or returns non-ok response
 */
export async function fetchChartData(
  dataId: string,
  sessionId?: string | null,
  userId?: string | null
): Promise<ChartDataPoint[]> {
  // Use Next.js API route as proxy to avoid CORS issues
  const params = new URLSearchParams();
  if (sessionId) {
    params.append('session_id', sessionId);
  }
  if (userId) {
    params.append('user_id', userId);
  }
  const url = `/api/chart-data/${dataId}${params.toString() ? '?' + params.toString() : ''}`;

  try {
    const response = await fetch(url);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      logger.error(`Chart API error response:`, errorData);
      throw new Error(
        `Failed to fetch chart data: ${response.status} ${response.statusText}`
      );
    }

    const responseData = await response.json();

    // Handle wrapped response format: { id: "...", data: [...] }
    let data: ChartDataPoint[];

    if (typeof responseData === 'object' && responseData !== null && 'data' in responseData) {
      // Response is wrapped with metadata
      const apiResponse = responseData as ChartApiResponse;

      // Validate that the ID matches what we requested
      if (apiResponse.id !== dataId) {
        logger.warn(`[Chart Utils] ID mismatch: requested ${dataId}, received ${apiResponse.id}`);
      }

      data = apiResponse.data;
    } else if (Array.isArray(responseData)) {
      // Response is directly an array (fallback)
      data = responseData;
    } else {
      throw new Error(
        `Invalid chart data format: expected array or {id, data} object, got ${typeof responseData}`
      );
    }

    if (!Array.isArray(data)) {
      throw new Error(
        `Invalid chart data format: data field is not an array, got ${typeof data}`
      );
    }

    return data;
  } catch (error) {
    logger.error(`Error fetching chart data for ${dataId}:`, error);
    throw error;
  }
}

/**
 * Transforms backend data to candlestick chart format
 * @param rawData - Array of data points from backend
 * @returns Array of candlestick data for lightweight-charts
 */
export function transformToCandlestickData(
  rawData: ChartDataPoint[]
): CandlestickData[] {
  return rawData.map((point) => ({
    time: parseTimeString(point.time),
    open: point.Open,
    high: point.High,
    low: point.Low,
    close: point.Close,
  }));
}

/**
 * Transforms backend data to line chart format
 * Uses the Close price as the value for each data point
 * @param rawData - Array of data points from backend
 * @returns Array of line data for lightweight-charts
 */
export function transformToLineData(rawData: ChartDataPoint[]): LineData[] {
  return rawData.map((point) => ({
    time: parseTimeString(point.time),
    value: point.Close,
  }));
}
